import { useEffect } from "react";
import { usePrintOnDemandProduct } from "@design-lab/entities/product";
import { useWarmChooseSizesPage } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { useIsSample } from "@design-lab/shared/lib/config";
import type { UseFormReturn } from "react-hook-form";
import type { CompositionsForm } from "@design-lab/shared/types";

const DEFAULT_CATALOG_ID = "G500";

type UseDefaultProductInitializationProps = {
  hasDesignProject: boolean;
  isLoadingProjectData: boolean;
  isEditMode: boolean;
  hasRestoredRef: { current: boolean };
  compositionForm: UseFormReturn<CompositionsForm>;
  hasCatalogFromUrl?: boolean;
};

export function useDefaultProductInitialization({
  hasDesignProject,
  isLoadingProjectData,
  isEditMode,
  hasRestoredRef,
  compositionForm,
  hasCatalogFromUrl = false
}: UseDefaultProductInitializationProps) {
  const warmChooseSizesPage = useWarmChooseSizesPage();
  const { registerValidationForAllCompositions } = useCompositionForm();
  const isSample = useIsSample();

  // In sample mode, don't load default product - let user choose from available sample products
  // Load default product only if not in sample mode, no design project exists, and no catalog from URL
  const { data: defaultProduct, isLoading: isLoadingDefaultProduct } = usePrintOnDemandProduct(
    !isSample && !hasDesignProject && !isLoadingProjectData && !hasCatalogFromUrl
      ? DEFAULT_CATALOG_ID
      : undefined
  );

  // Initialize with default product if no design project exists (and not in sample mode)
  useEffect(() => {
    if (
      hasRestoredRef.current ||
      isLoadingProjectData ||
      hasDesignProject ||
      !defaultProduct ||
      isEditMode ||
      isSample // Don't auto-select in sample mode
    ) {
      return;
    }

    const defaultComposition = defaultProduct.compositions.find(c => c.default);
    if (defaultComposition) {
      // Check if we already have a composition set (potentially from PDP)
      const existingCompositions = compositionForm.getValues("addedCompositions");
      const hasExistingComposition = existingCompositions && existingCompositions.length > 0;

      // Only set if no composition exists
      if (!hasExistingComposition) {
        compositionForm.setValue("currentCompositionId", defaultComposition.id);
        compositionForm.setValue("addedCompositions", [
          {
            id: defaultComposition.id,
            catalogId: DEFAULT_CATALOG_ID,
            colorName: defaultComposition.colorName,
            variants: {}
          }
        ]);
      }

      // Register validation for the default composition
      registerValidationForAllCompositions();

      // Warm cache for inventory and price data using product id
      if (defaultProduct?.id) {
        warmChooseSizesPage(defaultProduct.id);
      }

      // eslint-disable-next-line no-param-reassign
      hasRestoredRef.current = true;
    }
  }, [
    hasDesignProject,
    defaultProduct,
    isLoadingProjectData,
    isEditMode,
    compositionForm,
    hasRestoredRef,
    warmChooseSizesPage,
    registerValidationForAllCompositions,
    isSample
  ]);

  return {
    defaultProduct,
    isLoadingDefaultProduct
  };
}
